// core/Player.ts
import * as THREE from 'three';
import SceneManager from '../SceneManager';
import { playerOrbitController } from '../../services/movement/PlayerOrbitController';
import { global } from '../GlobalState';
import { ClientStateOrbContext } from './ClientStateOrbContext';
export class Player {
    constructor(init, controls, hud, input) {
        var _a, _b, _c, _d, _e;
        this.isLocal = true;
        this.sm = SceneManager.getInstance();
        // Motion state
        this.velocity = new THREE.Vector3();
        this.yaw = 0;
        this.pitch = 0;
        // Modes
        this.hyperspeed = false;
        this.warpInProgress = false;
        // Tuning
        this.movementDrag = 8;
        this.movementSpeed = 16;
        this.id = init.id;
        this.camera = (_a = init.camera) !== null && _a !== void 0 ? _a : new THREE.PerspectiveCamera((_b = init.fov) !== null && _b !== void 0 ? _b : 70, window.innerWidth / window.innerHeight, (_c = init.near) !== null && _c !== void 0 ? _c : 1, (_d = init.far) !== null && _d !== void 0 ? _d : 20000);
        if (init.startPosition) {
            this.camera.position.copy(init.startPosition);
        }
        else if (!init.camera) {
            this.camera.position.copy(new THREE.Vector3(0, 1, 4));
        }
        // IMPORTANT: use standard camera order for yaw/pitch
        this.camera.rotation.order = 'YXZ';
        this.currentSceneName = (_e = init.startScene) !== null && _e !== void 0 ? _e : 'default';
        this.controls = controls;
        this.input = input;
        this.hud = hud;
        this.raycaster = new THREE.Raycaster();
        this.clientStateContext = new ClientStateOrbContext({
            clientId: this.id,
            camera: this.camera,
            controls: this.input,
            controlsManager: this.controls,
        });
        // attach camera to scene graph
        this.sm.getScene(this.currentSceneName).add(this.camera);
        // keep aspect updated
        window.addEventListener('resize', () => {
            const w = window.innerWidth, h = window.innerHeight;
            this.camera.aspect = w / h;
            this.camera.updateProjectionMatrix();
        });
    }
    //this method might be unnecessary or at lest privateable
    setOrb(orb) {
        this.clientStateContext.bindOrb(orb);
    }
    getOrb() { return this.clientStateContext.getOrb(); }
    syncClientOrbStateNow() {
        this.syncOrb();
    }
    setScene(sceneName) {
        if (sceneName === this.currentSceneName)
            return;
        const oldParent = this.camera.parent;
        if (oldParent)
            oldParent.remove(this.camera);
        this.sm.getScene(sceneName).add(this.camera);
        this.currentSceneName = sceneName;
    }
    setHyperspeed(enabled) {
        this.hyperspeed = enabled;
        if (enabled) {
            this.movementDrag = 4;
            this.movementSpeed = 800;
        }
        else {
            this.movementDrag = 8;
            this.movementSpeed = 16;
        }
    }
    applySceneMovementPolicy() {
        const pos = this.camera.position;
        if (global.cachedHasFloorCap && pos.y < global.cachedFloorY) {
            pos.y = global.cachedFloorY;
        }
        else if (!global.cachedHasFloorCap && pos.y < 2) {
            pos.y = 2;
        }
        if (global.cachedHasRadiusCap) {
            const dist = Math.hypot(pos.x, pos.y, pos.z);
            if (dist > global.cachedRadiusCap) {
                const scale = global.cachedRadiusCap / dist;
                pos.multiplyScalar(scale);
            }
        }
        // hook in cached scene policies here later if desired
    }
    syncOrb() {
        this.clientStateContext.syncFromRuntime({
            hyperspeed: this.hyperspeed,
            movementSpeed: this.movementSpeed,
            movementDrag: this.movementDrag,
        });
    }
    teleportTo(pos) {
        this.velocity.set(0, 0, 0);
        this.camera.position.copy(pos);
    }
    update(dt) {
        var _a;
        if (!this.input.isLocked) {
            this.syncOrb();
            return;
        }
        // 1) Controls
        const intent = this.controls.sampleIntent();
        this.yaw += intent.deltaYaw;
        this.pitch = THREE.MathUtils.clamp(this.pitch + intent.deltaPitch, -Math.PI / 2, Math.PI / 2);
        this.camera.rotation.set(this.pitch, this.yaw, 0, 'YXZ');
        if (intent.toggleHyperspeed) {
            this.setHyperspeed(!this.hyperspeed);
        }
        const movementDelta = Math.min(dt, 0.05) * (1000 / 200);
        // 2) Drag (stable for any dt)
        const dragFactor = Math.exp(-this.movementDrag * movementDelta);
        this.velocity.multiplyScalar(dragFactor);
        // 3) Basis vectors
        const forward = new THREE.Vector3();
        this.camera.getWorldDirection(forward);
        forward.y = 0;
        forward.normalize();
        const up = new THREE.Vector3(0, 1, 0);
        const right = new THREE.Vector3().crossVectors(forward, up).normalize();
        // 4) Accel from intent (treat movementSpeed as accel)
        if (intent.moveForwardBack !== 0)
            this.velocity.addScaledVector(forward, intent.moveForwardBack * this.movementSpeed * movementDelta);
        if (intent.moveLeftRight !== 0)
            this.velocity.addScaledVector(right, intent.moveLeftRight * this.movementSpeed * movementDelta);
        if (intent.moveUpDown !== 0)
            this.velocity.addScaledVector(up, intent.moveUpDown * this.movementSpeed * movementDelta);
        playerOrbitController.update({
            deltaSeconds: Math.min(dt, 0.05),
            velocity: this.velocity,
            controls: this.input,
            camera: this.camera,
        });
        // 5) Integrate (project world velocity onto camera-relative axes)
        const rightVelocity = this.velocity.dot(right);
        const forwardVelocity = this.velocity.dot(forward);
        this.input.moveRight(rightVelocity * movementDelta);
        this.input.moveForward(forwardVelocity * movementDelta);
        this.input.getObject().position.y += this.velocity.y * movementDelta;
        if (global.autofly.active) {
            const autoflyDirection = new THREE.Vector3();
            this.camera.getWorldDirection(autoflyDirection);
            const speed = (_a = global.autofly.speed) !== null && _a !== void 0 ? _a : 120;
            this.input.getObject().position.addScaledVector(autoflyDirection, speed * movementDelta);
        }
        // 6) Scene constraints + HUD
        this.applySceneMovementPolicy();
        this.hud.update(this.sm.getScene(this.currentSceneName));
        // 7) Keep avatar orb glued
        this.syncOrb();
    }
}
